import {
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { DoraSelect } from "dora-shared/frontend";
import { UFMuiTextField } from "../../../UFMuiTextField";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as actions from "../../../../ducks/data/deliveries";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { useL10n } from "../../../../l10n";
import { selectExpenses } from "../../../../ducks/data/deliveries/selectors";
import { useMemo, useState } from "react";
import { selectExpenseAccounts } from "../../../../ducks/data/expenseAccounts/selectors";
import { syncExpenseAccounts } from "../../../../ducks/data/expenseAccounts";
import { SyncIconButton } from "../../../buttons";
import { DateTime } from "luxon";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { notify } from "../../../../ducks/notifications";

const EditExpenseLineModal = (props: {
  onClose: () => void;
  id: { deliveryId: string; expenseId: string };
}) => {
  const { id, onClose } = props;
  const { t } = useTranslation(["translation", "expenses", "components"]);
  const l10n = useL10n();
  const dispatch = useAppDispatch();
  const expenses = useSelector(selectExpenses(id.deliveryId));
  const expense = useMemo(
    () => expenses.find((x) => x.id === id.expenseId) || null,
    [expenses, id.expenseId]
  );
  const expenseAccounts = useSelector(selectExpenseAccounts);
  const [reqInProgress, setReqInProgress] = useState(false);
  const onSyncAccountsClick = () => {
    setReqInProgress(true);
    // A failed sync is reported and shown as a toast by the thunk itself.
    dispatch(syncExpenseAccounts()).finally(() => setReqInProgress(false));
  };
  const options = expenseAccounts.map((x) => ({
    id: x.id,
    label: `${x.accountNumber} - ${x.name}`,
  }));
  const validationSchema = useMemo(
    () =>
      yup.object({
        amount: yup
          .number()
          .label("Beløb")
          .typeError("${path} skal være et tal") // eslint-disable-line
          .transform((_, strValue) => l10n.parseNumber(strValue)),
      }),
    [l10n]
  );
  const defaultCurrency = "DKK";
  const { control, handleSubmit } = useForm({
    mode: "onTouched",
    // `raw`: validate with the schema but submit the values as typed; the
    // amount is parsed explicitly in onSubmit.
    resolver: yupResolver(validationSchema, undefined, { raw: true }),
    defaultValues: {
      accountId: expense?.accountId || null,
      currency: expense?.currency || defaultCurrency,
      amount: l10n.invariantToLocalizedNumberString(expense?.amount) || "",
      date: expense ? DateTime.fromISO(expense.date) : DateTime.now(),
    },
  });
  const onSubmit = handleSubmit(async (formValues) => {
    const accountId = formValues.accountId;
    if (!accountId) {
      return; // Mostly to keep typescript happy. Field is required
    }
    try {
      if (expense) {
        await dispatch(
          actions.updateExpense({
            ...formValues,
            ...id,
            accountId,
            amount: l10n.parseNumber(formValues.amount).toString(),
            date: formValues.date.toISODate(),
          })
        )?.unwrap();
      } else {
        await dispatch(
          actions.addExpense({
            ...formValues,
            ...id,
            accountId,
            amount: l10n.parseNumber(formValues.amount).toString(),
            date: formValues.date.toISODate(),
          })
        )?.unwrap();
      }
      onClose();
    } catch {
      dispatch(
        notify({
          message: t("translation:technicalError"),
          type: "error",
        })
      );
    }
  });
  return (
      <Dialog
        open={true}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <form onSubmit={onSubmit}>
          <DialogTitle>
            {expense
              ? t("expenses:expenseLine.editExpense")
              : t("expenses:expenseLine.newExpense")}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid size={8} className="flex items-center gap-1">
                <div className="flex-1">
                  <Controller
                    name="accountId"
                    control={control}
                    render={({ field }) => (
                      <DoraSelect
                        options={options.map((o) => ({
                          value: o.id,
                          label: o.label,
                        }))}
                        value={field.value || undefined}
                        onChange={(v) => field.onChange(v ?? null)}
                        label={t("expenses:expenseLine.account")}
                        autofocus
                      />
                    )}
                  />
                </div>
                <SyncIconButton
                  aria-label={t("expenses:expenseLine.syncAccounts")}
                  tooltipTitle={t("expenses:expenseLine.syncAccounts")}
                  onClick={onSyncAccountsClick}
                  disabled={reqInProgress}
                />
              </Grid>
              <Grid size={4}>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      // mask="____/__/__"
                      label={t("expenses:expenseLine.date")}
                      value={field.value}
                      onChange={(date) => {
                        field.onChange(date);
                      }}
                      slotProps={{
                        field: { openPickerButtonPosition: "start" } as any,
                        inputAdornment: { position: "start" },
                        textField: { margin: "dense" },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={8}>
                <UFMuiTextField
                  control={control}
                  required
                  label={t("expenses:expenseLine.amount")}
                  name="amount"
                />
              </Grid>
              <Grid size={4}>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <DoraSelect
                      options={[
                        { value: "DKK", label: "DKK" },
                        { value: "EUR", label: "EUR" },
                      ]}
                      value={field.value}
                      onChange={(v) => field.onChange(v)}
                      label={t("expenses:expenseLine.currency")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>
              {t("components:buttonLabels.cancel")}
            </Button>
            <Button type="submit">
              {t("components:buttonLabels.save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
  );
};

export default EditExpenseLineModal;
